import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import PageLayout from '@/components/Layout/PageLayout';
import DateRangePicker from '@/components/molecules/DateRangePicker/DateRangePicker';
import { useCage } from '@/contexts/cage';
import useCageFilter from '@/contexts/cage-filter-context';
import { isValidCageId } from '@/domain/cages';
import { useGetHealthOverview } from '@/domain/health';
import { useDateRangePicker } from '@/hooks/useDateRangePicker';

import { HEALTH_DEFAULT_DATE_RANGE_PRESET_ID } from '../../components/healthDetailChartConstants';
import HealthWoundsBodySubTabBar from '../components/HealthWoundsBodySubTabBar';
import HealthWoundsPrimaryTabBar from '../components/HealthWoundsPrimaryTabBar';

import HealthWoundsBodyGraphsView from './HealthWoundsBodyGraphsView';

export default function HealthWoundsBodyGraphsContainer() {
    const { t } = useTranslation('healthModule');
    const { cageId, loading: cageLoading } = useCage();
    const { daypartTag, selectedFishGroupId } = useCageFilter();

    const { dateFrom, dateTo, onDateChange } = useDateRangePicker({
        key: 'wounds-graphs',
        defaultPresetId: HEALTH_DEFAULT_DATE_RANGE_PRESET_ID,
    });

    const overviewParams = useMemo(
        () => ({
            daypartTag,
            from: dateFrom,
            to: dateTo,
            fishGroupId: selectedFishGroupId,
        }),
        [daypartTag, dateFrom, dateTo, selectedFishGroupId]
    );

    const {
        data: overview,
        isLoading: overviewLoading,
        isFetching,
    } = useGetHealthOverview(cageId, overviewParams, {
        enabled: isValidCageId(cageId) && Boolean(dateFrom && dateTo),
        staleTime: 5 * 60 * 1000,
    });

    const isLoading = cageLoading || overviewLoading || isFetching;
    const trendPoints = overview?.charts.woundDailyTrend ?? [];

    return (
        <PageLayout title={t('routes.wounds')} subtitle={t('routes.health')}>
            <HealthWoundsPrimaryTabBar />
            <HealthWoundsBodySubTabBar
                trailing={
                    <DateRangePicker
                        disabled={isLoading}
                        menuPosition="right"
                        defaultPresetId={HEALTH_DEFAULT_DATE_RANGE_PRESET_ID}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        onDateChange={onDateChange}
                    />
                }
            />
            <HealthWoundsBodyGraphsView trendPoints={trendPoints} isLoading={isLoading} />
        </PageLayout>
    );
}
